<template>
  <Card
    class="details-card"
    :class="[`tone-${tone}`, { 'has-body': $slots.default }]"
    :variant="tone === 'secondary' ? 'outline-secondary' : 'primary'"
    size="large"
  >
    <template #header>
      <View justify="space-between" align="start" gap="md" class="heading">
        <View direction="column" gap="xs" class="titles">
          <View as="span" align="center" gap="sm" wrap="wrap">
            <Text variant="h4" as="h3" class="title">
              <slot name="title">{{ title }}</slot>
            </Text>
            <slot name="badge" />
          </View>
          <Text v-if="subtitle || $slots.subtitle" variant="body-small" color="secondary" class="subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </Text>
        </View>

        <div v-if="$slots.action" class="action" :class="{ 'reveal-on-hover': actionable }">
          <slot name="action" />
        </div>
      </View>
    </template>

    <View v-if="$slots.default" direction="column" gap="md" class="body">
      <slot />
    </View>
  </Card>
</template>

<script setup lang="ts">
import { Card, Text, View } from '@tallo/design-system';

/**
 * One section of a details page: a title, an optional line under it, an action at the corner,
 * and the rows or placeholder below. A secondary tone sets apart what is only a default.
 *
 * The corner action of an `actionable` card is an edit pencil that waits for hover. The public listing passes
 * `actionable: false`: its corner holds Book a tour, which must show without hovering.
 */
withDefaults(
  defineProps<{ title?: string; subtitle?: string; tone?: 'primary' | 'secondary'; actionable?: boolean }>(),
  {
    title: undefined,
    subtitle: undefined,
    tone: 'primary',
    actionable: true,
  },
);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// Doubled to outrank the card's own size padding
.details-card.details-card {
  display: flex;
  flex-direction: column;
  padding: 1.25rem var(--t-spacing-lg);

  @include mobile {
    padding: var(--t-spacing-md);
  }
}

.heading {
  width: 100%;
}

.titles {
  min-width: 0;
}

.title {
  margin: 0;
}

.action {
  flex-shrink: 0;
  // Cancels the ghost button's padding so the pencil sits in the corner, centred on the title line,
  // without making the header taller than the title
  margin: -0.5rem -0.5rem -0.5rem 0;
  transition: opacity 250ms ease-in-out;
}

// Hidden until the card is hovered where hovering exists; touch screens can't hover, so it stays visible there
.action.reveal-on-hover {
  @media (hover: hover) {
    opacity: 0;
  }
}

.details-card:hover .action,
.details-card:focus-within .action {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .action {
    transition: none;
  }
}

// Fills the card, so a notes block can pin itself to the bottom of a stretched row
.body {
  flex: 1;
}
</style>
